import { useState, useEffect } from 'react';
import { Loader2, CalendarPlus, X, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useScheduleVisit, useClientPlannedVisits } from './hooks/useWeeklyVisits';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function getTomorrowDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
}

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

interface ScheduleVisitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitTargetId: number;
  clientId: number;
  clientName?: string;
}

export function ScheduleVisitDialog({
  open,
  onOpenChange,
  visitTargetId,
  clientId,
  clientName,
}: ScheduleVisitDialogProps) {
  // Дата начала: по умолчанию завтра
  const [startDate, setStartDate] = useState<string>(getTomorrowDate);

  // Дедлайн: по умолчанию = дате начала
  const [deadline, setDeadline] = useState<string>(getTomorrowDate);

  // Время визита (опционально)
  const [plannedTime, setPlannedTime] = useState<string>('');

  // Сбрасываем даты при открытии
  useEffect(() => {
    if (open) {
      const tomorrow = getTomorrowDate();
      setStartDate(tomorrow);
      setDeadline(tomorrow);
      setPlannedTime('');
    }
  }, [open]);

  // При смене даты начала — обновляем дедлайн
  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    setDeadline(value); // Синхронизируем дедлайн с датой начала
  };

  // Запланированные визиты этого клиента
  const { data: plannedVisits, isLoading: previewLoading } = useClientPlannedVisits(
    clientId,
    open // enabled only when dialog is open
  );

  const scheduleVisit = useScheduleVisit();

  // Валидация: deadline >= startDate
  const isDeadlineValid = !deadline || deadline >= startDate;

  const handleConfirm = () => {
    if (!startDate) return;

    scheduleVisit.mutate(
      {
        visitTargetId,
        date: startDate,
        deadline: deadline || undefined,
        plannedTime: plannedTime || undefined,
      },
      {
        onSuccess: () => onOpenChange(false),
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Запланировать визит</AlertDialogTitle>
          {clientName && (
            <AlertDialogDescription>{clientName}</AlertDialogDescription>
          )}
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Дата начала */}
          <div className="space-y-2">
            <Label htmlFor="startDate">Дата начала</Label>
            <div className="flex gap-2">
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                min={getTodayDate()}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleStartDateChange('')}
                disabled={!startDate}
                title="Очистить"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Визит появится в списке с этой даты
            </p>
          </div>

          {/* Дедлайн */}
          <div className="space-y-2">
            <Label htmlFor="deadline">Дедлайн (опционально)</Label>
            <div className="flex gap-2">
              <Input
                id="deadline"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={startDate || getTodayDate()}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeadline('')}
                disabled={!deadline}
                title="Очистить"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {deadline && (
              <p className="text-xs text-muted-foreground">
                Визит станет срочным с {formatDate(deadline)}
              </p>
            )}
            {!isDeadlineValid && (
              <p className="text-sm text-destructive">
                Дедлайн не может быть раньше даты начала
              </p>
            )}
          </div>

          {/* Время визита */}
          <div className="space-y-2">
            <Label htmlFor="plannedTime">Время визита (опционально)</Label>
            <div className="flex gap-2">
              <Input
                id="plannedTime"
                type="time"
                value={plannedTime}
                onChange={(e) => setPlannedTime(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPlannedTime('')}
                disabled={!plannedTime}
                title="Очистить"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Превью запланированных визитов клиента */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" />
              Запланировано у клиента
            </Label>
            <div className="border rounded-md p-2 bg-muted/30 min-h-[60px]">
              {previewLoading ? (
                <div className="flex items-center justify-center h-[60px] text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Загрузка...
                </div>
              ) : plannedVisits && plannedVisits.length > 0 ? (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {plannedVisits.map((visit) => (
                    <div
                      key={visit.id}
                      className="text-sm p-1.5 bg-background rounded flex justify-between items-center"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">
                          Начало: {formatDate(visit.plannedDate)}
                        </span>
                        {visit.deadline && (
                          <span className="text-xs text-orange-600 dark:text-orange-400">
                            Дедлайн: {formatDate(visit.deadline)}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {visit.status === 'PENDING' ? 'ожидает' : visit.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[60px] text-muted-foreground text-sm">
                  Нет запланированных визитов
                </div>
              )}
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!startDate || !isDeadlineValid || scheduleVisit.isPending}
          >
            {scheduleVisit.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CalendarPlus className="h-4 w-4 mr-2" />
            )}
            Запланировать
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
